import Types from "../actions/types";
import { sendSucceeded, sendFailed, sendRequested, removeQueueItem } from "../actions";
import { ofType } from "redux-observable";
import { authHeaders } from "frame/auth-headers";
import { tokenExists } from "frame/access-token";
import { mergeMap, catchError, exhaustMap } from "rxjs/operators";
import { of, EMPTY, from } from "rxjs";
import { ajax } from "rxjs/ajax";
import { DBService, JOBS_TABLE, replaceDataURLs } from "frame/indexedDBService";
import * as R from "ramda";
import { setGlobalMessage } from "features/jobs";
import { connect } from "react-redux";
import { logQueueError } from "utils";

// Classify HTTP errors as permanent or temporary
const classifyError = (error) => {
	if (!error || !error.status) {
		// Network errors, timeouts, etc. are temporary
		return { isPermanent: false, message: error?.message || "Network error" };
	}

	const status = error.status;
	
	// 401 Unauthorized is temporary - token may have expired, user can log back in
	// 408 Request Timeout and 429 Too Many Requests are temporary
	if (status === 401 || status === 408 || status === 429) {
		return { 
			isPermanent: false, 
			message: status === 401 ? "Authentication required. Please log in and try again." : (error.response?.message || `Server error (${status})`)
		};
	}
	
	// 4xx errors (except those above) are typically permanent
	// 400 Bad Request, 403 Forbidden, 404 Not Found, etc.
	if (status >= 400 && status < 500) {
		return { 
			isPermanent: true, 
			message: error.response?.message || `Server error (${status})` 
		};
	}
	
	// 5xx errors are temporary (should retry)
	return { 
		isPermanent: false, 
		message: error.response?.message || `Server error (${status})` 
	};
};

const mapDispatchToProps = {
    setGlobalMessage
};



/*
  request queue poll for photo
*/
// var queueId = "";
const queueSendEffect = (action$, state$) =>
    action$.pipe(
        ofType(Types.QUEUE_SEND_REQUESTED),
        // One in-flight queue send at a time so bursts of sendRequested() cannot POST the same head item in parallel.
        exhaustMap(() =>
            from(
                (async () => {
                    const db = new DBService();
                    return await db.getAll(JOBS_TABLE);
                })()
            ).pipe(
                mergeMap((result) => {
            // Only process queue if we have network AND a valid token
            if (
                state$.value.queue.list &&
                state$.value.queue.list.length > 0 &&
                state$.value.queue.hasNetwork &&
                tokenExists()
            ) {
                // If start+stop are both queued, drop start first and re-enter the processor.
                // Stop alone creates a completed timesheet (API fallback) and avoids a
                // transient Active row between start-sync and stop-sync.
                const orphanedStart = state$.value.queue.list.find(
                    (item) => item && item.url && /\/timesheet\/start$/i.test(item.url)
                );
                const hasQueuedTimesheetStop = state$.value.queue.list.some(
                    (item) => item && item.url && /\/timesheet\/stop$/i.test(item.url)
                );
                if (orphanedStart?.id && hasQueuedTimesheetStop) {
                    return of(removeQueueItem(orphanedStart.id), sendRequested());
                }

                // Find the first item that can be retried (not permanent failure and not exceeded max retries)
                const retryableItem = state$.value.queue.list.find((item) => {
                    if (!item) return false;
                    const maxRetries = item.maxRetries || 5;
                    const failedCount = item.failedCount || 0;
                    const isPermanentFailure = item.isPermanentFailure || false;
                    return !isPermanentFailure && failedCount < maxRetries;
                });

                if (!retryableItem) {
                    // No retryable items, skip processing
                    return EMPTY;
                }

                const currentQueueItem = retryableItem;
                const currentQueueItemId = currentQueueItem.id;
                let body = R.clone(currentQueueItem.body);

                // Only job-style payloads have attachments. Timesheets and other queued actions
                // may not include them, so guard to avoid crashing the queue processor.
                if (Array.isArray(body?.attachments) && Array.isArray(result)) {
                    body.attachments = replaceDataURLs(body.attachments, result);
                }

                // Get headers fresh right before making the request to ensure token is still valid
                const headers = authHeaders();
                
                // Double-check token is still valid right before request
                if (!tokenExists() || !headers.Authorization) {
                    // Token expired or missing, mark as failed (temporary) so it retries later
                    return of(
                        sendFailed(
                            currentQueueItemId,
                            "Authentication required. Please log in and try again.",
                            false // Temporary - will retry when user logs back in
                        ),
                        sendRequested() // Continue processing queue
                    );
                }

                return ajax
                    .post(currentQueueItem.url, body, headers)
                    .pipe(
                        mergeMap((ajaxResponse) =>
                            of(
                                sendSucceeded(
                                    currentQueueItemId,
                                    currentQueueItem.body?.id,
                                    currentQueueItem.url
                                ),
                                sendRequested(),
                                setGlobalMessage(state$.value.jobs.globalMessage.filter(o => o[0] != currentQueueItemId))
                            )
                        ),
                        catchError((error) => {
                            const errorInfo = classifyError(error);
                            const maxRetries = currentQueueItem.maxRetries || 5;
                            const currentFailedCount = currentQueueItem.failedCount || 0;
                            const willExceedMaxRetries = (currentFailedCount + 1) >= maxRetries;
                            const isPermanent = errorInfo.isPermanent || willExceedMaxRetries;

                            // Stale active-notes sync after the timesheet was already stopped:
                            // drop the queue item and let timesheet epics clear the Active flag.
                            const isActiveNotes =
                                currentQueueItem.url &&
                                /\/timesheet\/active\/notes/i.test(currentQueueItem.url);
                            if (isActiveNotes && (error?.status === 404 || error?.status === 400)) {
                                return of(
                                    removeQueueItem(currentQueueItemId),
                                    sendRequested()
                                );
                            }
                            
                            // Create queue item with error info for logging
                            const queueItemForLogging = {
                                ...currentQueueItem,
                                failedCount: currentFailedCount + 1,
                                isPermanentFailure: isPermanent,
                                lastError: errorInfo.message,
                            };
                            
                            // Extract user information from state
                            const profile = state$.value?.profile;
                            const organisations = state$.value?.organisations;
                            const currentOrganisationId = organisations?.currentOrganisation;
                            const currentOrganisation = organisations?.data?.find(org => org.id === currentOrganisationId);
                            
                            const user = profile ? {
                                id: profile.userId || profile.id,
                                username: profile.userName || profile.username,
                                email: profile.email,
                                organisationId: currentOrganisationId,
                                organisationName: currentOrganisation?.name,
                            } : null;
                            
                            // Log error to Rollbar
                            logQueueError(
                                error?.message || errorInfo.message || "Queue send failed",
                                queueItemForLogging,
                                user
                            );
                            
                            return of(
                                sendFailed(
                                    currentQueueItemId, 
                                    errorInfo.message, 
                                    isPermanent
                                ),
                                sendRequested(), // Continue processing queue
                                setGlobalMessage(state$.value.jobs.globalMessage.filter(o => o[0] != currentQueueItemId))
                            );
                        })
                    );
            } else {
                // If there is no item in the queue, ensure any stale sync messages are cleared
                if (state$.value.queue.list && state$.value.queue.list.length === 0) {
                    return of(setGlobalMessage([]));
                }
                return EMPTY;
            }
                })
            )
        )
    );


connect(mapDispatchToProps)(queueSendEffect);

export default queueSendEffect;